import React, { useRef, useState, useMemo, useEffect } from "react";
import JoditEditor from "jodit-react";

const normalizeContent = (html = "") => {
  if (!html) return "";

  const cleaned = html
    .replace(/<p><br><\/p>/gi, "")
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/<div><br><\/div>/gi, "")
    .trim();

  return cleaned;
};

const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Start typing...",
  readOnly = false,
}) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  const [content, setContent] = useState(() => normalizeContent(value));

  /* -------------------------------------------------
     SYNC EXTERNAL VALUE (EDIT PAGE SUPPORT)
  ------------------------------------------------- */
  useEffect(() => {
    const normalized = normalizeContent(value);

    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    if (normalized !== content) {
      setContent(normalized);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------------------------------
     JODIT CONFIG (STABLE & COMPACT)
  ------------------------------------------------- */
  const config = useMemo(
    () => ({
      readonly: readOnly,

      // 🔥 FIX: Disable placeholder when content exists
      placeholder: content ? "" : placeholder,

      height: "auto",
      minHeight: 120,
      maxHeight: 400,
      autoHeight: true,

      enter: "P",
      defaultBlockTag: "p",

      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      processPasteHTML: true,

      saveInterval: 0,
      observer: { timeout: 100 },

      toolbarAdaptive: false,
      toolbarSticky: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,

      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "link",
        "image",
        "|",
        "undo",
        "redo",
        "|",
        "source",
      ],

      cleanHTML: {
        removeEmptyElements: true,
        fillEmptyParagraph: false,
      },

      uploader: {
        insertImageAsBase64URI: true,
      },

      style: {
        fontSize: "14px",
        padding: "8px",
      },
    }),
    [placeholder, readOnly, content]
  );

  /* -------------------------------------------------
     HANDLE CHANGE (ON BLUR ONLY – STABLE)
  ------------------------------------------------- */
  const handleBlur = (newContent) => {
    const normalized = normalizeContent(newContent);

    isInternalChange.current = true;
    setContent(normalized);
    onChange?.(normalized);
  };

  return (
    <div className="border rounded-md bg-white p-1">
      <JoditEditor
        ref={editorRef}
        value={content}
        config={config}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default RichTextEditor;
